import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Menu, X, LogOut, ExternalLink } from "lucide-react"; // Hamburger, close, logout and external link icons
import { useUser } from "./components/UserContext";
import { Button } from "./components/ui/button"; // Import Button component

import { UserProvider } from "./components/UserContext";
import TourEditor from "./components/TourEditor";
import TourViewer from "./components/TourViewer";
import Dashboard from "./components/Dashboard";
import UploadImages from "./components/UploadImages";
import ImageSelector from "./components/ImageSelector";
import PublicTourRoute from "./components/PublicTourRoute";
import PublicSpaceRoute from "./components/PublicSpaceRoute";
import UserSelector from "./components/UserSelector";
import "./App.css";
import { Toaster } from "./components/ui/toaster";
import Signup from "./components/Signup";
import Login from "./components/Login";
import TourExplorer from "./components/TourExplorer";
import ContractorSelector from "./components/ContractorSelector";
import UserClientsManager from "./components/UserClientsManager";
import { AspectRatio } from "./components/ui/aspect-ratio";
import FileManager from "./components/FileManager";
import LidarViewer from "./pages/LidarViewer";
import MeasurementViewer from "./components/MeasurementViewer";
import PushQueueManager from "./components/PushQueueManager";
import SyncTourManager from "./components/SyncTourManager";
import ChecklistApprovalWorkflow from "./components/ChecklistApprovalWorkflow";
import * as Sentry from "@sentry/react";
// import Navbar from './components/Navbar';
import * as Tooltip from "@radix-ui/react-tooltip";

// Import tenant config
import { TENANT_ID, TENANT_NAME, SENTRY_DSN, WELCOME_TEXT, PRISMA_STUDIO_URL, LOGO_PATH, MAIN_IMAGE_PATH } from "./config";

// ServiceCallsDashboard is now a standalone HTML page at /service-calls-dashboard.html
const ServiceCallsRedirect = () => {
  React.useEffect(() => {
    window.location.href = '/service-calls-dashboard.html';
  }, []);
  return <div className="flex items-center justify-center h-screen">מעביר לדף קריאות שירות...</div>;
};

// Initialize Sentry with tenant-specific DSN
if (SENTRY_DSN) {
Sentry.init({
    dsn: SENTRY_DSN,
  tracesSampleRate: 1.0,
});
}

const Home = () => (
  <div className="flex flex-col min-h-[calc(100vh-4rem)]">
    {/* Hero Section with Responsive Image */}
    <div className="relative w-full flex-1 flex flex-col items-center justify-center">
      {/* Background Container with AspectRatio for mobile */}
      <div className="absolute inset-0 w-full h-full">
        <div className="w-full h-full md:hidden">
          <AspectRatio ratio={9 / 16} className="w-full h-full">
            <div
              className="w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${MAIN_IMAGE_PATH})` }}
            />
          </AspectRatio>
        </div>

        {/* Background for larger screens */}
        <div
          className="hidden md:block w-full h-full bg-cover bg-center"
          style={{ backgroundImage: `url(${MAIN_IMAGE_PATH})` }}
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-20 flex flex-col items-center text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 md:mb-10 text-white drop-shadow-lg">
          {WELCOME_TEXT}
        </h1>

        {/* Optional buttons can go here */}
        <div className="flex flex-col sm:flex-row gap-4 w-full max-w-sm sm:max-w-md">
          {/* Navigation buttons if needed */}
        </div>
      </div>
    </div>
  </div>
);

const AppContent = () => {
  const { selectedUserPermissions, setSelectedUser, setSelectedUserPermissions, hasAnyPermission } =
    useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Logout handler function
  const handleLogout = () => {
    // Clear all user-related localStorage items
    localStorage.removeItem("selectedUser");
    localStorage.removeItem("selectedUserPermissions");
    localStorage.removeItem("userPermission");
    localStorage.removeItem("tourExplorer_location");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userInfo");
    localStorage.removeItem("userToken");

    // Update context
    if (setSelectedUser) setSelectedUser(null);
    if (setSelectedUserPermissions) setSelectedUserPermissions([]);

    // Redirect to login page
    navigate("/login");
  };

  const pages = [
    { label: "Home", path: "/", element: <Home />, roles: ["ALL"] },

    { label: "Login", path: "/login", element: <Login />, roles: ["GUEST"] },
    { label: "Signup", path: "/signup", element: <Signup />, roles: ["GUEST"] },

    {
      label: "Upload Images",
      path: "/upload-images",
      element: <UploadImages />,
      roles: ["ADMIN_USER", "FULL_EDITOR", "UPLOADER_IMAGE"],
    },
    {
      label: "Images to Tour",
      path: "/images-tour",
      element: <ImageSelector />,
      roles: ["ADMIN_USER", "FULL_EDITOR"],
    },
    {
      label: "Tour Editor",
      path: "/tour-editor",
      element: <TourEditor />,
      roles: ["ADMIN_USER", "FULL_EDITOR"],
    },
    {
      label: "Tour Editor",
      path: "/tour-editor/:id",
      element: <TourEditor />,
      roles: ["ADMIN_USER", "FULL_EDITOR"],
    },
    {
      label: "Tour Viewer",
      path: "/tour-viewer",
      element: <TourViewer />,
      roles: ["ADMIN_USER", "FULL_EDITOR", "FULL_VIEWER"],
    },
    {
      label: "Tour Explorer",
      path: "/tour-explorer",
      element: <TourExplorer />,
      roles: [
        "ADMIN_USER",
        "FULL_EDITOR",
        "UPLOADER_IMAGE",
        "FULL_VIEWER",
        "CLIENT_VIEWER",
        "BEDEK",
      ],
    },
    {
      label: "קריאות שירות",
      path: "/service-calls",
      element: <ServiceCallsRedirect />,
      roles: [
        "ADMIN_USER",
        "BEDEK",
      ],
      tenants: ["agamim"], // Only show for agamim tenant
    },
    {
      label: "File Manager",
      path: "/file-manager",
      element: <FileManager />,
      roles: ["ADMIN_USER"],
    },
    {
      label: "User-Client Manager",
      path: "/user-clients",
      element: <UserClientsManager />,
      roles: ["ADMIN_USER"],
    },
    // Routes available by direct URL but hidden from navigation
    {
      label: "LiDAR Viewer",
      path: "/lidar",
      element: <LidarViewer />,
      roles: ["ADMIN_USER"],
      hideFromNavigation: true,
    },
    {
      label: "Measurement Viewer",
      path: "/measurement",
      element: <MeasurementViewer />,
      roles: ["ADMIN_USER", "FULL_EDITOR", "FULL_VIEWER"],
      hideFromNavigation: true,
    },
    {
      label: "Push Queue",
      path: "/push-queue",
      element: <PushQueueManager />,
      roles: ["ADMIN_USER", "FULL_EDITOR"],
      hideFromNavigation: true,
    },
    {
      label: "Sync Tours",
      path: "/sync-tours",
      element: <SyncTourManager />,
      roles: ["ADMIN_USER", "FULL_EDITOR"],
      hideFromNavigation: true,
    },
    {
      label: "אישור צ'קליסט",
      path: "/checklist-approval",
      element: <ChecklistApprovalWorkflow />,
      roles: ["ADMIN_USER", "FULL_EDITOR", "CHECKLIST_ONLY"],
      tenants: ["tidharm"],
    },
  ];
  const isGuest = !selectedUserPermissions || selectedUserPermissions.length === 0;
  
  // Filter pages based on user permissions and tenant (supports multiple roles)
  const navLinks = pages.filter(
    (page) =>
      !page.path.includes(":id") &&
      !page.hideFromNavigation &&
      (!page.tenants || page.tenants.includes(TENANT_ID)) && // Tenant filter
      (page.roles.includes("ALL") || 
       (isGuest && page.roles.includes("GUEST")) ||
       (!isGuest && hasAnyPermission(page.roles))),
  );
  const routeComponents = pages.filter(
    (page) => 
      (!page.tenants || page.tenants.includes(TENANT_ID)) && // Tenant filter
      (page.roles.includes("ALL") || 
              (isGuest && page.roles.includes("GUEST")) ||
              (!isGuest && hasAnyPermission(page.roles))),
  );

  const showLogoutButton = !isGuest;

  return (
    <>
      {/*<UserSelector />*/}
      <div
        dir="ltr"
        className="flex flex-col min-h-screen bg-background text-foreground"
      >
        {/*<div>{selectedUserPermission}</div>*/}
        <header className="border-b bg-card relative z-50">
          <div className="container mx-auto flex flex-col items-start sm:items-center justify-between gap-4 px-4 py-4">
            <div className="flex w-full items-center justify-between">
              <img src={LOGO_PATH} alt="Logo" className="h-8 w-auto" />

              {/* Mobile menu button */}
              <button
                className="sm:hidden text-foreground z-50 relative"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <Menu className="h-6 w-6 pointer-events-auto" />
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden sm:flex gap-4 items-center">
              {navLinks.map(({ label, path }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${
                      isActive
                        ? "text-primary font-semibold border-b-2 border-primary"
                        : "text-muted-foreground hover:text-primary"
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}

              {/* Show Prisma Studio link for admin users only */}
              {hasAnyPermission(['ADMIN_USER']) && PRISMA_STUDIO_URL && (
                <a
                  href={PRISMA_STUDIO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium transition-colors text-muted-foreground hover:text-primary flex items-center gap-1"
                >
                  Prisma Studio
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}

              {/* Show logout button if user is logged in */}
              {showLogoutButton && (
                <div className="ml-4 pl-4 border-l border-gray-200">
                  <button
                    onClick={handleLogout}
                    className="text-sm font-medium transition-colors text-muted-foreground hover:text-primary flex items-center gap-1"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Navigation Menu - Using state instead of Dialog */}
            {mobileMenuOpen && (
              <div className="fixed inset-0 z-50 bg-black/50 sm:hidden">
                <div className="fixed top-0 left-0 z-50 w-64 h-full bg-white shadow-lg overflow-y-auto border-r border-gray-200">
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-6">
                      <img src={LOGO_PATH} alt="Logo" className="h-8 w-auto" />
                      <button
                        className="p-2 rounded-full hover:bg-gray-100"
                        onClick={closeMobileMenu}
                        aria-label="Close menu"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <nav className="flex flex-col gap-4">
                      {navLinks.map(({ label, path }) => (
                        <NavLink
                          key={path}
                          to={path}
                          onClick={closeMobileMenu}
                          className={({ isActive }) =>
                            `text-base font-medium text-left p-2 rounded-md transition-colors ${
                              isActive
                                ? "text-primary bg-primary/10"
                                : "text-gray-700 hover:bg-gray-100 hover:text-primary"
                            }`
                          }
                        >
                          {label}
                        </NavLink>
                      ))}

                      {/* Show Prisma Studio link for admin users only */}
                      {hasAnyPermission(['ADMIN_USER']) && (
                        <a
                          href="https://sbw9rm8u73.eu-central-1.awsapprunner.com/"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMobileMenu}
                          className="text-base font-medium text-left p-2 rounded-md transition-colors text-gray-700 hover:bg-gray-100 hover:text-primary flex items-center gap-2"
                        >
                          Prisma Studio
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}

                      {/* Add logout button in mobile menu */}
                      {showLogoutButton && (
                        <button
                          onClick={() => {
                            closeMobileMenu();
                            handleLogout();
                          }}
                          className="text-base font-medium text-left p-2 rounded-md transition-colors text-gray-700 hover:bg-gray-100 hover:text-primary flex items-center gap-2"
                        >
                          <LogOut className="h-4 w-4" />
                          Logout
                        </button>
                      )}
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 container mx-auto px-4 py-8 w-full">
          <Routes>
            {routeComponents.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </main>

        <Toaster />
      </div>
    </>
  );
};

function App() {
  const location = window.location.pathname;
  const isPublicTour = location.startsWith("/public-tour/");
  const isPublicSpace = location.startsWith("/spaceModal/");
  const isPublic = isPublicTour || isPublicSpace;

  if (isPublic) {
    return (
      <Router>
        <Tooltip.Provider>
          {" "}
          {/* <-- WRAP HERE */}
          <Routes>
            <Route path="/public-tour/:tourId" element={<PublicTourRoute />} />
            <Route
              path="/spaceModal/:spaceIdSupernumber"
              element={<PublicSpaceRoute />}
            />
          </Routes>
        </Tooltip.Provider>
        <Toaster />
      </Router>
    );
  }

  return (
    <Tooltip.Provider>
      {" "}
      {/* <-- WRAP HERE */}
      <UserProvider>
        <Router>
          {/*<Navbar />*/}
          <AppContent />
        </Router>
      </UserProvider>
    </Tooltip.Provider>
  );
}

export default App;
